import { Typography, Grid, Paper, Skeleton } from "@mui/material";
import { People, Chat, Group } from "@mui/icons-material";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { CustomTooltip, KpiCard } from "../home";
import { useEffect, useState } from "react";
import {
  getUsers,
  getConversations,
  getApiKey,
  getFeedback,
  getMetrics,
} from "modules/agentMySecurity/services/embeddingsService";
import {
  AbsoluteZIndexHeader,
  CardHeader,
  CustomPieText,
} from "./dashboard.styled";
import {
  Conversation,
  FeedbackUsageCount,
  GroupUsageCount,
  Metrics,
  ModelUsageCount,
} from "../admin.type";
import { useAppTheme } from "core/contexts/theme/theme";

// Overview Tab
export const OverviewTab = () => {
  const { apptheme } = useAppTheme();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetricsData] = useState<Metrics | null>(null);
  const [users, setUsers] = useState(null);
  const [newUsers, setNewUsers] = useState([]);
  const [groupUsage, setGroupUsage] = useState<GroupUsageCount[]>([]);
  const [modelUsage, setModelUsage] = useState<ModelUsageCount[]>([]);
  const [feedbackUsage, setFeedbackUsage] = useState<FeedbackUsageCount[]>([]);

  async function fetchConversations() {
    try {
      // Fetch conversations from the API
      const list = await getConversations();

      // Return the list of conversations (this is where you handle the list)
      return list;
    } catch (error) {
      // Handle errors
      console.error("Error fetching conversations:", error);
    }
  }

  // Fetch data from backend
  useEffect(() => {
    const fetchData = async () => {
      // In production, you would make actual API calls here
      // These would be the endpoints you need to create or already have
      try {
        // Fetch metrics to, avoid having to pull more data than necessary
        const metrics = await fetchMetrics();
        setMetricsData(metrics);

        // Fetch users
        const users = await fetchUsers();
        setUsers(users);

        // User/Group association graph
        const groupDistribution: any = getGroupDistribution(users);
        setGroupUsage(groupDistribution);

        // Tally of positive/negative feedback
        const feedbackDistribution: FeedbackUsageCount[] =
          await getFeedbackDistributionData();
        setFeedbackUsage(feedbackDistribution);

        // Fetch the list of API Keys
        const newUsers: any = fetchNewUsersPerDay(users);
        setNewUsers(newUsers);

        // Fetch the model usage per conversation
        const conversations = await fetchConversations();
        const modelUsage: any = getModels(conversations);
        setModelUsage(modelUsage);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (feedbackUsage.length > 0) {
      setLoading(false);
    }
  }, [feedbackUsage]);

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    index,
  }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <CustomPieText
        x={x}
        y={y}
        fill={apptheme.colors.textColor.slice(1)}
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </CustomPieText>
    );
  };

  return (
    <Grid container spacing={3}>
      {/* KPI Cards Row */}
      <Grid item xs={12} lg={8}>
        <Grid container spacing={3}>
          <Grid item xs={6} sm={4} md={3}>
            <KpiCard
              title="Total Users"
              value={loading || metrics === null ? null : metrics.totalUsers}
              icon={<People />}
              loading={loading}
              navigateTo={"/console/secai/admin/users"}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <KpiCard
              title="Invites Sent"
              value={loading || metrics === null ? null : metrics.invitesSent}
              icon={<Group />}
              loading={loading}
              navigateTo={"/console/secai/admin/invite"}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <KpiCard
              title="Total Conversations"
              value={loading || metrics === null ? null : metrics.conversations}
              icon={<Chat />}
              loading={loading}
              navigateTo={null}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <KpiCard
              title="Active API Keys"
              value={loading || metrics === null ? null : metrics.activeApiKeys}
              icon={<Group />}
              loading={loading}
              navigateTo={"/console/secai/admin/apikey"}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <KpiCard
              title="Reports"
              value={loading || metrics === null ? null : metrics.reports}
              icon={<Group />}
              loading={loading}
              navigateTo={"/console/secai/admin/reports"}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <KpiCard
              title="Registered IAUs"
              value={loading || metrics === null ? null : metrics.iaus}
              icon={<Group />}
              loading={loading}
              navigateTo={"/console/secai/admin/iaus"}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <KpiCard
              title="Groups"
              value={loading || metrics === null ? null : metrics.adGroups}
              icon={<Group />}
              loading={loading}
              navigateTo={"/console/secai/admin/groups"}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <KpiCard
              title="Feedback"
              value={loading || metrics === null ? null : metrics.feedback}
              icon={<Group />}
              loading={loading}
              navigateTo={"/console/secai/admin/feedback"}
            />
          </Grid>
        </Grid>
      </Grid>

      {/* Feedback Distribution */}
      <Grid item xs={12} md={4} xl={3}>
        <Paper sx={{ p: 2, height: "100%" }} className="position-relative">
          <AbsoluteZIndexHeader>User Feedback</AbsoluteZIndexHeader>

          {loading ? (
            <Skeleton
              variant="rectangular"
              height={285}
              animation="wave"
              sx={{ top: "40px" }}
            />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={feedbackUsage}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={90}
                  dataKey="value"
                  stroke={apptheme.colors.textColorOpposite}
                  strokeWidth={5}
                  label={renderCustomizedLabel}
                >
                  {feedbackUsage.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.name === "Positive" ? "#4caf50" : "#f44336"}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Grid container spacing={3}>
          {/* New Users This Month */}
          <Grid item xs={12} md={12}>
            <Paper className="p-2">
              <CardHeader>New Users This Month</CardHeader>
              {loading ? (
                <Skeleton variant="rectangular" height={300} animation="wave" />
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={newUsers}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis label={{ fill: apptheme.colors.textColor }} />
                    <Tooltip
                      content={<CustomTooltip />}
                      cursor={{ fill: apptheme.colors.barChartBarFullWBgHv }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="#8884d8"
                      name="New Users"
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>

          {/* User Group Distribution */}
          <Grid item xs={12} md={12}>
            <Paper className="p-2">
              <CardHeader>Current User Group Association</CardHeader>

              {loading ? (
                <Skeleton variant="rectangular" height={300} animation="wave" />
              ) : (
                <ResponsiveContainer
                  width="100%"
                  height={Math.max(400, groupUsage.length * 40)}
                >
                  <BarChart
                    layout="vertical"
                    data={groupUsage}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 0,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis
                      label={{
                        value: "Groups",
                        angle: 270,
                        position: "left",
                        offset: 10,
                        style: { textAnchor: "middle" },
                        fill: apptheme.colors.textColor,
                      }}
                      dataKey="name"
                      stroke={apptheme.colors.textColor}
                      type="category"
                      width={200}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip
                      content={<CustomTooltip />}
                      cursor={{ fill: apptheme.colors.barChartBarFullWBgHv }}
                    />
                    <Legend />
                    <Bar
                      dataKey="value"
                      name="Users"
                      fill={apptheme.colors.barChartBarBg}
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Grid>

      {/* Model Usage Distribution */}
      <Grid item xs={12} md={6}>
        <Paper className="p-2">
          <CardHeader>Model Usage Distribution</CardHeader>
          {loading ? (
            <Skeleton variant="rectangular" height={300} animation="wave" />
          ) : (
            <ResponsiveContainer
              width="100%"
              height={Math.max(400, (modelUsage || "1").length * 40)}
            >
              <BarChart
                layout="vertical"
                data={modelUsage}
                margin={{
                  top: 20,
                  right: 30,
                  left: 0,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />

                <YAxis
                  label={{
                    value: "Groups",
                    angle: 270,
                    position: "left",
                    offset: 10,
                    style: { textAnchor: "middle" },
                    fill: apptheme.colors.textColor,
                  }}
                  dataKey="model"
                  stroke={apptheme.colors.textColor}
                  type="category"
                  width={200}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip
                  content={<CustomTooltip />}
                  cursor={{ fill: apptheme.colors.barChartBarFullWBgHv }}
                />

                <Legend />
                <Bar
                  fill={apptheme.colors.barChartBarBg}
                  dataKey="value"
                  name="Conversations"
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

const getModels = (conversations: Conversation[]): ModelUsageCount[] => {
  const totalConversations = conversations.length;

  // Count occurrences of each model
  const modelCounts = conversations.reduce(
    (acc: { [key: string]: number }, conversation) => {
      const model = conversation.model;
      acc[model] = (acc[model] || 0) + 1;
      return acc;
    },
    {}
  );

  // Convert to array and sort by count
  const sortedModels = Object.entries(modelCounts)
    .map(
      ([model, value]): ModelUsageCount => ({
        model,
        value,
        percentage: ((value / totalConversations) * 100).toFixed(1) + "%",
      })
    )
    .sort((a, b) => b.value - a.value);
  return sortedModels;
};


const fetchNewUsersPerDay = (users) => {
    const newUsersPerDay: any = [];
  
    // Get the current date and calculate the date one month ago
    const currentDate = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(currentDate.getMonth() - 1);
  
    // Loop through each API key to gather active users by day
    users.forEach((key) => {
      const createdDate = new Date(key.createdAt);
      const owner = key._id;
  
      // Only process keys created within the last month
      if (createdDate >= oneMonthAgo) {
        // Format the date as "YYYY-MM-DD"
        const dateString = createdDate.toISOString().split("T")[0];
  
        // Find index of this date in our array
        const existingIndex = newUsersPerDay.findIndex(
          (data) => data.date === dateString
        );
  
        if (existingIndex === -1) {
          // If no data for this day, create a new entry with a new Set
          newUsersPerDay.push({
            date: dateString,
            newResources: new Set([owner]),
          });
        } else {
          // Update the existing entry by adding this user to the set
          newUsersPerDay[existingIndex].newResources.add(owner);
        }
      }
    });
  
    // Convert Sets to arrays for proper serialization before logging or returning
    const result = newUsersPerDay.map((item) => ({
      date: item.date,
      count: item.newResources.size,
      users: Array.from(item.newResources),
    }));
  
    return result;
};
  

async function fetchApiKeys() {
  try {
    const response = await getApiKey();
    return response;
  } catch (error) {
    console.error("Error fetching api keys:", error);
  }
}

const getFeedbackDistributionData = async () => {
  try {
    const feedbackData = await getFeedback();

    if (!feedbackData || !Array.isArray(feedbackData)) {
      throw new Error("Invalid feedback data");
    }
    let positiveCount = 0;
    let negativeCount = 0;
    feedbackData.forEach((feedback) => {
      if (feedback.negative) {
        negativeCount++;
      } else {
        positiveCount++;
      }
    });
    return [
      { name: "Positive", value: positiveCount },
      { name: "Negative", value: negativeCount },
    ];
  } catch (error) {
    console.error("Unable to generate feedback distribution data: ", error);
    return [
      { name: "Positive", value: 0 },
      { name: "Negative", value: 0 },
    ];
  }
};

async function fetchMetrics() {
  try {
    const response = await getMetrics();
    if (response === null) {
      // Return a default metrics object to prevent null reference errors
      return {
        totalUsers: 0,
        invitesSent: 0,
        conversations: 0,
        activeApiKeys: 0,
        reports: 0,
        iaus: 0,
        adGroups: 0,
        feedback: 0
      };
    }
    return response;
  } catch (error) {
    console.error("Error fetching metrics:", error);
    // Return a default metrics object to prevent null reference errors
    return {
      totalUsers: 0,
      invitesSent: 0,
      conversations: 0,
      activeApiKeys: 0,
      reports: 0,
      iaus: 0,
      adGroups: 0,
      feedback: 0
    };
  }
}

async function fetchUsers() {
  try {
    const response = await getUsers();
    if (response === null) {
      return [];
    }
    return response;
  } catch (error) {
    console.error("Error fetching users:", error);
    return [];
  }
}

function getGroupDistribution(data) {
  try {
    const distro = {};
    data.forEach((user) => {
      user.groups.forEach((group) => {
        if (distro[group]) {
          distro[group] += 1;
        } else {
          distro[group] = 1;
        }
      });
    });

    const result = Object.keys(distro).map((group) => {
      return { name: group, value: distro[group] };
    });

    return result;
  } catch (error) {
    return null;
  }
}
